/**
 * Generic debounce hook.
 *
 * Type parameter: T — the type of the value being debounced
 *
 * Params:
 *   - value: T     — the rapidly-changing value to debounce
 *   - delay: number — debounce delay in milliseconds
 *
 * Returns:
 *   - T — the debounced value; only updates after `delay` ms of inactivity
 *
 * Behaviour:
 *   - Each time `value` changes, the internal timer resets
 *   - After `delay` ms without a change, the returned value updates
 *   - Timer is cleared on unmount to prevent state updates on unmounted components
 *
 * Example:
 *   const debouncedSearch = useDebounce(searchTerm, 300);
 *   // debouncedSearch only changes after the user stops typing for 300ms
 *
 *   useEffect(() => {
 *     if (debouncedSearch) fetchResults(debouncedSearch);
 *   }, [debouncedSearch]);
 */

import { useState, useEffect } from 'react';

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup: clear the timer if value changes before delay elapses,
    // or if the component unmounts.
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}
